import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import {
  clearOpsAnalyticsToken,
  fetchAnalyticsOverview,
  fetchAnalyticsTools,
  getOpsAnalyticsToken,
  setOpsAnalyticsToken
} from '../lib/opsAnalytics'
import type { AnalyticsDays, AnalyticsOverview, AnalyticsTools } from '../lib/opsAnalytics'

const ranges: AnalyticsDays[] = [7, 30]

const OpsAnalyticsPage = () => {
  const [tokenInput, setTokenInput] = useState('')
  const [token, setToken] = useState(() => getOpsAnalyticsToken())
  const [days, setDays] = useState<AnalyticsDays>(7)
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [tools, setTools] = useState<AnalyticsTools | null>(null)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const loadDashboard = useCallback(async (activeToken: string, selectedDays: AnalyticsDays) => {
    setIsLoading(true)
    setError('')

    try {
      const [overviewData, toolsData] = await Promise.all([
        fetchAnalyticsOverview(selectedDays, activeToken),
        fetchAnalyticsTools(selectedDays, activeToken)
      ])
      setOverview(overviewData)
      setTools(toolsData)
    } catch (err) {
      const message = err instanceof Error ? err.message : '统计数据暂时不可用，请稍后重试'
      setError(message)
      if (/访问令牌无效/i.test(message)) {
        clearOpsAnalyticsToken()
        setToken('')
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!token) return
    void loadDashboard(token, days)
  }, [days, loadDashboard, token])

  const handleUnlock = async (event: FormEvent) => {
    event.preventDefault()
    const trimmed = tokenInput.trim()
    if (!trimmed) {
      setError('请输入访问令牌')
      return
    }
    setOpsAnalyticsToken(trimmed)
    setToken(trimmed)
    await loadDashboard(trimmed, days)
  }

  const toolRows = useMemo(() => tools?.rows || [], [tools])

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-6xl">
        <header className="border-b border-slate-200 pb-6">
          <h1 className="text-3xl font-semibold text-slate-950">运营统计看板</h1>
          <p className="mt-3 max-w-3xl text-slate-600">
            查看 Parentool 的流量来源、落地页和工具使用表现，用于首页排序和工具体验优化。
          </p>
        </header>

        {!token && (
          <form className="mt-8 max-w-xl rounded-2xl border border-slate-200 bg-white p-5" onSubmit={handleUnlock}>
            <label className="block text-sm font-medium text-slate-700">
              访问令牌
              <input
                className="input-field mt-2"
                type="password"
                value={tokenInput}
                onChange={event => setTokenInput(event.target.value)}
              />
            </label>
            <button className="btn mt-4 bg-slate-950 text-white" type="submit">
              进入看板
            </button>
            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          </form>
        )}

        {token && (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="flex gap-2">
                {ranges.map(range => (
                  <button
                    key={range}
                    className={`btn ${days === range ? 'bg-slate-950 text-white' : 'bg-white text-slate-900 ring-1 ring-slate-200'}`}
                    onClick={() => setDays(range)}
                  >
                    {range}天
                  </button>
                ))}
              </div>
              <button className="btn bg-white text-slate-900 ring-1 ring-slate-200" onClick={() => void loadDashboard(token, days)}>
                刷新
              </button>
            </div>

            {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
            {isLoading && <p className="mt-4 text-sm text-slate-600">统计数据加载中...</p>}

            {overview && (
              <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">总会话</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">{overview.summary.sessions}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">页面浏览</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">{overview.summary.pageViews}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">工具成功使用</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">{overview.summary.toolSuccesses}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">最高来源</p>
                  <p className="mt-2 text-lg font-semibold text-slate-950">{overview.summary.topSource}</p>
                </div>
              </section>
            )}

            {overview && (
              <section className="mt-6 grid gap-6 xl:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h2 className="text-lg font-semibold text-slate-950">流量来源</h2>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-left text-slate-500">
                        <tr>
                          <th className="pb-2">来源</th>
                          <th className="pb-2 text-right">会话</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.topSources.map(item => (
                          <tr key={item.source} className="border-t border-slate-100">
                            <td className="py-2 text-slate-800">{item.source}</td>
                            <td className="py-2 text-right text-slate-800">{item.sessions}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <h2 className="text-lg font-semibold text-slate-950">热门落地页</h2>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-left text-slate-500">
                        <tr>
                          <th className="pb-2">路径</th>
                          <th className="pb-2 text-right">浏览量</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overview.topLandingPages.map(item => (
                          <tr key={item.path} className="border-t border-slate-100">
                            <td className="py-2 text-slate-800">{item.path}</td>
                            <td className="py-2 text-right text-slate-800">{item.pageViews}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {toolRows.length > 0 && (
              <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
                <h2 className="text-lg font-semibold text-slate-950">工具表现</h2>
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-slate-500">
                      <tr>
                        <th className="pb-2">工具</th>
                        <th className="pb-2 text-right">浏览</th>
                        <th className="pb-2 text-right">提交</th>
                        <th className="pb-2 text-right">成功</th>
                        <th className="pb-2 text-right">成功率</th>
                      </tr>
                    </thead>
                    <tbody>
                      {toolRows.map(row => (
                        <tr key={row.toolId} className="border-t border-slate-100">
                          <td className="py-2 text-slate-800">
                            <div className="font-medium">{row.toolName}</div>
                            <div className="text-xs text-slate-500">{row.toolCategory}</div>
                          </td>
                          <td className="py-2 text-right text-slate-800">{row.viewCount}</td>
                          <td className="py-2 text-right text-slate-800">{row.submitCount}</td>
                          <td className="py-2 text-right text-slate-800">{row.successCount}</td>
                          <td className="py-2 text-right text-slate-800">{Math.round(row.successRate * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default OpsAnalyticsPage
